import type { ImageData } from '@beat-venues/common-types';
import { ContentfulContentImage } from '@beat-venues/contentful-image';
import type { Document } from '@contentful/rich-text-types';

import {
    Button,
    Heading,
    RichText,
    TextLink,
    TextLinkProps,
} from '../../atoms';
import { getIconComponent } from '../../atoms/icon/icon.utils';
import type { CommonProps } from '../../types';
import { tv, type VariantProps } from 'tailwind-variants';

import type { JSX } from 'react';

export interface TileProps
    extends CommonProps,
        VariantProps<typeof TileStyles> {
    title: string;
    description?: Document;
    image?: ImageData;
    icon?: string;
    locale: string;
    link?: Pick<TextLinkProps, 'href' | 'locale'>;
    cta?: { label: string; href: string; onClick?: () => void };
}

export const TileStyles = tv({
    slots: {
        root: 'click-area group text-tile overflow-hidden md:flex flex-col md:gap-4',
        imageContainer: 'overflow-hidden',
        iconContainer:
            'mx-auto text-center flex flex-col justify-center aspect-square w-1/4 md:w-1/3 mt-2',
        image: 'transition group-hover:scale-105 rounded-none',
        icon: 'max-h-full max-w-full',
        textContainer: 'flex flex-col gap-4 px-3 py-2 md:px-5 md:py-4',
        heading: 'leading-none md:text-xl mb-0 text-center',
        link: 'click-area-link text-inherit hover:no-underline',
    },
    variants: {
        size: {
            sm: {
                root: 'bg-tile hover:bg-tileHover rounded-md',
                textContainer: 'items-center',
                link: 'h-full overflow-hidden items-start',
            },
            lg: {
                root: 'bg-tile hover:bg-tileHover rounded-md',
                imageContainer: 'aspect-video flex items-center',
                iconContainer: '',
                textContainer: 'items-center',
            },
            round: {
                imageContainer: 'aspect-square rounded-full',
                iconContainer:
                    'bg-tile group-hover:bg-tileHover w-full h-auto aspect-square rounded-full p-6 items-center',
                image: 'aspect-square h-auto w-full object-cover',
                textContainer: 'items-start pt-6',
                heading: 'text-left',
            },
        },
        hasIcon: {
            true: '',
        },
        hasImage: {
            true: '',
        },
        invertFontColor: {
            true: { textContainer: 'text-black', iconContainer: 'text-black' },
            false: {},
        },
        hasDescription: {
            true: '',
        },
        hasCta: {
            true: {
                root: 'hover:bg-tile',
                image: 'group-hover:scale-100',
                textContainer: 'flex-1',
            },
        },
    },
    compoundVariants: [
        // Mobility tiles collection
        {
            hasIcon: true,
            size: 'lg',
            hasDescription: true,
            class: {
                root: 'p-3 md:py-8',
            },
        },
        // Plan je bezoek kleine slider tiles
        {
            hasIcon: true,
            size: 'lg',
            hasDescription: false,
            class: {
                root: 'w-full flex flex-col justify-center md:gap-0',
                textContainer: '',
            },
        },
        // Jobs venue
        {
            hasIcon: false,
            size: 'sm',
            class: {
                root: '',
            },
        },
        {
            hasImage: true,
            size: 'sm',
            class: {
                heading: 'text-left font-medium',
                textContainer:
                    'items-start p-4 absolute top-0 left-0 w-full h-full md:p-4',
            },
        },
    ],
    defaultVariants: {
        size: 'lg',
        hasIcon: false,
    },
});

export const Tile = ({
    title,
    image,
    icon,
    link,
    locale,
    description,
    cta,
    testId = 'tile',
    invertFontColor = false,
    size = 'lg',
    ...props
}: TileProps): JSX.Element => {
    const {
        root,
        imageContainer,
        iconContainer,
        image: imageStyle,
        icon: iconStyle,
        heading,
        textContainer,
        link: linkStyle,
    } = TileStyles({
        size,
        hasIcon: !!icon,
        hasImage: !!image,
        invertFontColor,
        hasDescription: !!description,
        hasCta: !!cta,
    });

    return (
        <div className={root()} {...props} data-testid={testId}>
            {image && (
                <div className={imageContainer()}>
                    <ContentfulContentImage
                        className={imageStyle()}
                        data={{
                            ...image,
                            fields: { ...image.fields, altText: '' },
                        }}
                        showCaption={false}
                    />
                </div>
            )}

            {icon && (
                <div className={iconContainer()}>
                    {getIconComponent(icon, {
                        className: iconStyle(),
                        size: 'auto',
                    })}
                </div>
            )}

            <div className={textContainer()}>
                <TextLink
                    className={cta ? undefined : linkStyle()}
                    enableDrag={false}
                    underline={false}
                    {...(link
                        ? { href: link.href, locale: link.locale }
                        : undefined)}
                >
                    <Heading as="h3" className={heading()} size="h5">
                        {title}
                    </Heading>
                </TextLink>

                {description && size !== 'sm' && (
                    <RichText
                        body={description}
                        locale={locale}
                        disableMultiline={true}
                        {...(size === 'round'
                            ? { className: 'hyphens-auto' }
                            : undefined)}
                    />
                )}
                {cta && (
                    <Button
                        href={cta.href}
                        onClick={cta.onClick}
                        className="mx-auto mb-8 mt-auto"
                    >
                        {cta.label}
                    </Button>
                )}
            </div>
        </div>
    );
};
